/* ── AI Gateway ────────────────────────────────────────────────────────────
 *
 * The admin surfaces for the AI half of API Management:
 *
 *   AiProvidersPage   upstream LLM accounts — keys, models, list prices
 *   AiUsagePage       what was spent, by whom, on what
 *   ProductAiTab      an AI product's gateway config, inside the product panel
 *   ProductAiUsageTab that product's own consumption
 *   PortalAiPanel     the customer-facing model catalogue and playground
 *
 * A provider's API key is write-only everywhere in here. It is posted once on
 * create, masked on every read, and there is no reveal endpoint to call — the
 * only consumer of the plaintext value is the gateway's secret manager, over
 * loopback. Nothing on this page can display one, which is deliberate: an
 * operator who can see a key can paste it somewhere it should not be.
 *
 * Every top-level name in this file is unique to it. Static scripts share one
 * global scope and Babel parses them all before React mounts, so a duplicated
 * `function` silently shadows the other definition and a duplicated top-level
 * `const` is a hard SyntaxError that blanks the entire SPA.
 */

const AI_PROVIDER_LABELS={
  'openai':'OpenAI','openai-compatible':'OpenAI-compatible','azure-openai':'Azure OpenAI',
  'anthropic':'Anthropic','deepseek':'DeepSeek','gemini':'Google Gemini','vertex-ai':'Vertex AI',
  'bedrock':'AWS Bedrock','openrouter':'OpenRouter','aimlapi':'AI/ML API',
  'lakera':'Lakera Guard','aws-comprehend':'AWS Comprehend'
};

/**
 * Accounts that are not models: the services a guardrail plugin calls out to. They live
 * beside the LLM providers because they are stored and resolved the same way, but they
 * are offered only in the Guardrails section, never as a model instance.
 */
const AI_GUARDRAIL_KINDS=['lakera','aws-comprehend'];
function aiIsGuardrail(p){return AI_GUARDRAIL_KINDS.indexOf(p)>=0;}

/** How a driver authenticates — 'aws' (SigV4), 'gcp' (service account) or 'key'. */
function aiAuthKind(p){
  if(p==='bedrock'||p==='aws-comprehend')return 'aws';
  if(p==='vertex-ai')return 'gcp';
  return 'key';
}

/**
 * Tile colour and fallback monogram per provider.
 *
 * The actual brand marks live in `ai-logos.js` (vendored, MIT) and are used where
 * one exists. These monograms cover the two entries that have no vendor mark:
 * aimlapi, and openai-compatible — which is a protocol rather than a company, so
 * `{ }` is the honest mark for it rather than someone else's logo.
 *
 * The colour is still used for every provider: it tints the tile behind the logo.
 */
const AI_PROVIDER_BRAND={
  'openai':            {mono:'OA', color:'#10A37F'},
  'anthropic':         {mono:'AN', color:'#D97757'},
  'gemini':            {mono:'GM', color:'#4285F4'},
  'azure-openai':      {mono:'AZ', color:'#0078D4'},
  'deepseek':          {mono:'DS', color:'#4D6BFE'},
  'openrouter':        {mono:'OR', color:'#6467F2'},
  'aimlapi':           {mono:'ML', color:'#0EA5E9'},
  'bedrock':           {mono:'BR', color:'#FF9900'},
  'vertex-ai':         {mono:'VX', color:'#34A853'},
  'openai-compatible': {mono:'{ }', color:'#64748B'},
  'lakera':            {mono:'LK', color:'#7C3AED'},
  'aws-comprehend':    {mono:'CM', color:'#FF9900'}
};

/** Picker order — the ones most people want first, the special cases last. */
const AI_PROVIDER_ORDER=['openai','anthropic','gemini','azure-openai','deepseek',
                         'openrouter','aimlapi','bedrock','vertex-ai','openai-compatible',
                         'lakera','aws-comprehend'];

function aiProviderLabel(p){return AI_PROVIDER_LABELS[p]||p||'—';}

function aiBrand(p){return AI_PROVIDER_BRAND[p]||{mono:'AI',color:'#64748B'};}

/**
 * The provider's brand mark in a tinted tile, reused by the picker, the provider
 * cards and the editor header.
 *
 * Falls back to a monogram when there is no vendored mark, so a driver added to the
 * gateway before its logo is vendored still renders as a proper tile rather than a
 * gap.
 */
function AiProviderMark({provider,size}){
  const b=aiBrand(provider);
  const s=size||40;
  const logo=(window.AI_PROVIDER_LOGOS||{})[provider];
  const tile={width:s,height:s,borderRadius:s*0.28,flex:'none',display:'inline-flex',
              alignItems:'center',justifyContent:'center',background:b.color+'1f'};

  if(logo){
    return(
      <span style={tile}>
        <span aria-hidden="true"
              style={{width:s*0.58,height:s*0.58,display:'block',
                      // A monochrome mark follows the theme's text colour so it stays
                      // legible in dark mode; a polychrome one carries its own.
                      color:logo.mono?'var(--text)':undefined}}
              dangerouslySetInnerHTML={{__html:logo.svg}}/>
      </span>
    );
  }
  return(
    <span style={{...tile,color:b.color,fontWeight:700,fontSize:s*0.34,letterSpacing:'-0.02em'}}>
      {b.mono}
    </span>
  );
}

function aiMoney(v){
  const n=Number(v||0);
  if(n===0)return '$0.00';
  // Sub-cent totals are the norm on a single call, so show enough places to be
  // non-zero rather than rounding an honest number down to nothing.
  return n<0.01?'$'+n.toFixed(6):'$'+n.toFixed(2);
}

function aiNum(v){return Number(v||0).toLocaleString();}

function aiTokenQuotaLabel(q){
  if(!q||!q.enabled)return 'Unmetered';
  const win=q.timeWindowSeconds>=86400?(q.timeWindowSeconds/86400)+'d'
           :q.timeWindowSeconds>=3600?(q.timeWindowSeconds/3600)+'h'
           :q.timeWindowSeconds+'s';
  if(q.strategy==='cost')return '$'+Number(q.budgetUsd||0).toLocaleString()+' spend / '+win;
  return aiNum(q.limit)+' '+(q.strategy||'total_tokens').replace(/_/g,' ')+' / '+win;
}

/**
 * Choose a provider to add. Clicking a tile opens the editor with that driver
 * already selected, which is the only thing the driver dropdown was ever for.
 *
 * `types` comes from the server, so a driver the running gateway does not have is
 * simply not offered — the list is not hardcoded here beyond ordering and branding.
 */
function AiProviderPicker({types,onPick,compact}){
  const available=(types||[]).map(t=>t.id);
  const ordered=[
    ...AI_PROVIDER_ORDER.filter(id=>available.indexOf(id)>=0),
    ...available.filter(id=>AI_PROVIDER_ORDER.indexOf(id)<0)
  ];
  const list=ordered.length?ordered:AI_PROVIDER_ORDER;
  const groups=[
    {title:'Models',ids:list.filter(id=>!aiIsGuardrail(id))},
    {title:'Guardrail services',ids:list.filter(aiIsGuardrail)}
  ].filter(g=>g.ids.length);

  return(
    <div style={{display:'grid',gap:16}}>
    {groups.map(g=>(
    <div key={g.title}>
    {groups.length>1&&<div style={{fontSize:11,fontWeight:600,color:'var(--text3)',textTransform:'uppercase',
                                    letterSpacing:'.04em',marginBottom:8}}>{g.title}</div>}
    <div style={{display:'grid',gap:10,
                 gridTemplateColumns:'repeat(auto-fill,minmax('+(compact?170:200)+'px,1fr))'}}>
      {g.ids.map(id=>{
        const t=(types||[]).find(x=>x.id===id)||{};
        const auth=aiAuthKind(id);
        return(
          <button key={id} type="button" onClick={()=>onPick(id)} title={'Add '+aiProviderLabel(id)}
            style={{display:'flex',alignItems:'center',gap:12,padding:'12px 14px',borderRadius:10,
                    background:'var(--surface)',border:'1px solid var(--border)',cursor:'pointer',
                    textAlign:'left',width:'100%',transition:'border-color .12s, box-shadow .12s'}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor=aiBrand(id).color;
                              e.currentTarget.style.boxShadow='var(--shadow-sm)';}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';
                              e.currentTarget.style.boxShadow='none';}}>
            <AiProviderMark provider={id} size={compact?32:38}/>
            <span style={{minWidth:0}}>
              <span style={{display:'block',fontWeight:600,fontSize:13,color:'var(--text)'}}>
                {aiProviderLabel(id)}
              </span>
              <span style={{display:'block',fontSize:11,color:'var(--text3)',marginTop:1}}>
                {id==='lakera'?'Injection, jailbreak & PII screening'
                       :id==='aws-comprehend'?'Toxicity moderation'
                       :auth==='aws'?'AWS access key + region'
                       :auth==='gcp'?'Service account + project'
                       :t.requiresEndpoint?'Needs an endpoint'
                       :(t.suggestedModels&&t.suggestedModels.length)?t.suggestedModels[0]
                       :'Any compatible endpoint'}
              </span>
            </span>
          </button>
        );
      })}
    </div>
    </div>
    ))}
    </div>
  );
}

/* ══ Providers ═══════════════════════════════════════════════════════════ */

function AiProvidersPage({showToast,confirm}){
  const [providers,setProviders]=useState([]);
  const [types,setTypes]=useState([]);
  const [loading,setLoading]=useState(true);
  const [editing,setEditing]=useState(null);   // provider object, or {provider:id} for new
  const [picking,setPicking]=useState(false);
  const [testing,setTesting]=useState(null);

  const load=async()=>{
    setLoading(true);
    try{
      const [ps,ts]=await loadAll([
        ['Providers',API('GET','/api/admin/ai-providers'),[]],
        ['Provider types',API('GET','/api/admin/ai-providers/types'),[]],
      ],showToast);
      setProviders(ps||[]);setTypes(ts||[]);
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setLoading(false);}
  };
  useEffect(()=>{load();},[]);

  const remove=async(p)=>{
    const ok=confirm?await confirm({title:'Delete provider',
      message:'Delete "'+p.name+'"? Any AI product routing to it will stop working until it is repointed.',
      confirmLabel:'Delete',danger:true}):true;
    if(!ok)return;
    try{
      await API('DELETE','/api/admin/ai-providers/'+p.id);
      showToast&&showToast('Provider deleted','success');
      load();
    }catch(e){showToast&&showToast(e.message,'error');}
  };

  const test=async(p)=>{
    setTesting(p.id);
    try{
      const r=await API('POST','/api/admin/ai-providers/'+p.id+'/test');
      showToast&&showToast(r.message||(r.ok?'Provider accepted the credential':'Test failed'),r.ok?'success':'error');
      load();
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setTesting(null);}
  };

  return(
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">AI Providers</h1>
          <div className="page-subtitle">
            Upstream model accounts. Keys are stored encrypted and never written into gateway
            configuration — routes carry a reference the gateway resolves per request, so rotating
            a key here takes effect without republishing anything.
          </div>
        </div>
        {providers.length>0&&(
          <button className="btn btn-primary" style={typeof TAXLAB_CTA_STYLE!=='undefined'?TAXLAB_CTA_STYLE:{}}
                  onClick={()=>setPicking(true)}>Add provider</button>
        )}
      </div>

      {loading?<div className="card"><div className="card-body">Loading…</div></div>
       :providers.length===0?(
        // The empty state *is* the picker. A lone "Add provider" button on an
        // otherwise blank page costs a click to reach the same choice.
        <div className="card"><div className="card-body">
          <div style={{fontSize:15,fontWeight:600,marginBottom:4}}>Connect a provider</div>
          <div style={{color:'var(--text3)',fontSize:13,marginBottom:18}}>
            Pick a provider to add an account. You can add several — an AI product can
            balance across them and fall back when one is unavailable.
          </div>
          <AiProviderPicker types={types} onPick={id=>setEditing({provider:id})}/>
        </div></div>
      ):(
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))',gap:16}}>
          {providers.map(p=>(
            <div key={p.id} className="card">
              <div className="card-body">
                <div style={{display:'flex',alignItems:'flex-start',gap:12}}>
                  <AiProviderMark provider={p.provider}/>
                  <div style={{minWidth:0,flex:1}}>
                    <div style={{fontWeight:600,display:'flex',alignItems:'center',gap:8}}>
                      {p.name}
                      <span className={'badge badge-'+(p.active?'success':'gray')} style={{fontSize:10}}>
                        {p.active?'Active':'Inactive'}
                      </span>
                    </div>
                    <div style={{color:'var(--text3)',fontSize:12,marginTop:2}}>{aiProviderLabel(p.provider)}</div>
                  </div>
                </div>

                {p.description&&<div style={{fontSize:13,color:'var(--text2)',margin:'10px 0'}}>{p.description}</div>}

                <div style={{fontSize:12,color:'var(--text3)',marginTop:10,display:'grid',gap:4}}>
                  <div>Key: <span style={{fontFamily:'monospace'}}>{p.apiKey||'not set'}</span></div>
                  {p.accessKeyId&&<div>Access key id: <span style={{fontFamily:'monospace'}}>{p.accessKeyId}</span></div>}
                  {(p.region||p.projectId)&&<div>{p.projectId?('Project '+p.projectId+' · '):''}{p.region||''}</div>}
                  {aiIsGuardrail(p.provider)?<div>Guardrail service — used from a product's Guardrails section</div>:(
                    <React.Fragment>
                      <div>Models: {(p.models&&p.models.length)?p.models.join(', '):'—'}</div>
                      <div>Priced: {(p.pricing&&p.pricing.length)?p.pricing.length+' model'+(p.pricing.length>1?'s':''):'not priced'}</div>
                    </React.Fragment>
                  )}
                  {p.endpointOverride&&<div style={{wordBreak:'break-all'}}>Endpoint: {p.endpointOverride}</div>}
                  {p.lastTestResult&&<div>Last test: {p.lastTestResult}</div>}
                </div>

                <div style={{display:'flex',gap:8,marginTop:14,flexWrap:'wrap'}}>
                  <button className="btn btn-sm btn-secondary" onClick={()=>setEditing(p)}>Edit</button>
                  <button className="btn btn-sm btn-ghost" disabled={testing===p.id} onClick={()=>test(p)}>
                    {testing===p.id?'Testing…':'Test'}
                  </button>
                  <button className="btn btn-sm btn-danger" onClick={()=>remove(p)}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {picking&&(
        <div className="modal-overlay" onClick={()=>setPicking(false)}>
          <div className="modal" style={{maxWidth:640}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Add a provider</div>
              <button className="modal-close-btn" onClick={()=>setPicking(false)}>×</button>
            </div>
            <div className="modal-body"><div className="modal-body-scroll">
              <AiProviderPicker types={types} compact
                                onPick={id=>{setPicking(false);setEditing({provider:id});}}/>
            </div></div>
          </div>
        </div>
      )}

      {editing&&<AiProviderEditor provider={editing} types={types}
                                  onClose={()=>setEditing(null)}
                                  onSaved={()=>{setEditing(null);load();}}
                                  showToast={showToast}/>}
    </div>
  );
}

function AiProviderEditor({provider,types,onClose,onSaved,showToast}){
  const isNew=!provider.id;
  const [form,setForm]=useState({
    name:provider.name||'',description:provider.description||'',
    provider:provider.provider||'openai',
    apiKey:'',
    endpointOverride:provider.endpointOverride||'',
    region:provider.region||'',projectId:provider.projectId||'',accessKeyId:provider.accessKeyId||'',
    models:(provider.models||[]).join(', '),
    defaultModel:provider.defaultModel||'',
    active:provider.active!==false
  });
  const [pricing,setPricing]=useState(provider.pricing||[]);
  const [saving,setSaving]=useState(false);
  const nameRef=useRef(null);
  const keyRef=useRef(null);

  // Escape closes, since the overlay deliberately does not — see the note on the
  // overlay below.
  useEffect(()=>{
    const onKey=e=>{if(e.key==='Escape')onClose();};
    window.addEventListener('keydown',onKey);
    return()=>window.removeEventListener('keydown',onKey);
  },[onClose]);

  const typeInfo=(types||[]).find(t=>t.id===form.provider)||{};
  const needsEndpoint=!!typeInfo.requiresEndpoint;
  const authKind=aiAuthKind(form.provider);
  const guardrail=aiIsGuardrail(form.provider);
  const keyLabel=authKind==='aws'?'Secret access key':authKind==='gcp'?'Service account JSON':'API key';
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));

  const save=async()=>{
    // Read the DOM as well as React state for the two required fields.
    //
    // A password manager (and some autofill paths) writes input.value through the
    // native setter without dispatching an input event, so React never sees it:
    // the field looks filled to the operator while form.apiKey is still ''. The
    // save then stops at the guard below with a toast that is easy to miss, no
    // request is sent, and it reads as "the button does nothing".
    const name=(form.name||(nameRef.current&&nameRef.current.value)||'').trim();
    const apiKey=(form.apiKey||(keyRef.current&&keyRef.current.value)||'').trim();

    if(!name)return showToast&&showToast('Name is required','error');
    if(isNew&&!apiKey)return showToast&&showToast(keyLabel+' is required','error');
    if(needsEndpoint&&!form.endpointOverride.trim())
      return showToast&&showToast(aiProviderLabel(form.provider)+' has no default endpoint — set one','error');
    if(authKind!=='key'&&!form.region.trim())
      return showToast&&showToast(aiProviderLabel(form.provider)+' needs a region','error');
    if(authKind==='aws'&&!form.accessKeyId.trim())
      return showToast&&showToast('Access key id is required','error');
    if(authKind==='gcp'&&!form.projectId.trim())
      return showToast&&showToast('GCP project id is required','error');
    setSaving(true);
    try{
      const body={
        name:name,description:form.description.trim(),provider:form.provider,
        endpointOverride:form.endpointOverride.trim()||null,
        region:form.region.trim()||null,
        projectId:form.projectId.trim()||null,
        accessKeyId:form.accessKeyId.trim()||null,
        models:form.models.split(',').map(s=>s.trim()).filter(Boolean),
        defaultModel:form.defaultModel.trim()||null,
        active:form.active,
        pricing:pricing.filter(p=>p.model&&p.model.trim())
      };
      // A blank key means "leave it alone". Sending an empty string on edit is how
      // a stored credential gets destroyed by an unrelated change.
      if(apiKey)body.apiKey=apiKey;

      if(isNew)await API('POST','/api/admin/ai-providers',body);
      else await API('PUT','/api/admin/ai-providers/'+provider.id,body);
      showToast&&showToast('Provider saved','success');
      onSaved();
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setSaving(false);}
  };

  const addPrice=()=>setPricing(p=>[...p,{model:form.defaultModel||'',inputPer1M:'',outputPer1M:'',currency:'USD'}]);
  const setPrice=(i,k,v)=>setPricing(p=>p.map((row,j)=>j===i?{...row,[k]:v}:row));
  const delPrice=(i)=>setPricing(p=>p.filter((_,j)=>j!==i));

  // Deliberately no onClick={onClose} on the overlay. Every other modal in the app
  // closes on an outside click, but this is the one form where doing so discards a
  // freshly pasted API key with no warning and no way to recover it — the operator
  // has to go back to the provider's console and mint another. Cancel and × are the
  // ways out; Escape is wired below.
  return(
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:720}} onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <div className="card-title" style={{display:'flex',alignItems:'center',gap:10}}>
            <AiProviderMark provider={form.provider} size={30}/>
            {isNew?('Add '+aiProviderLabel(form.provider)):('Edit '+provider.name)}
          </div>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body-scroll">
          <div className="form-group">
            <label className="form-label">Name</label>
            <input className="form-input" ref={nameRef} value={form.name}
                   onChange={e=>set('name',e.target.value)} placeholder="Production OpenAI"/>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <input className="form-input" value={form.description} onChange={e=>set('description',e.target.value)}/>
          </div>
          <div className="form-group">
            <label className="form-label">Driver</label>
            <select className="form-input" value={form.provider} onChange={e=>set('provider',e.target.value)}>
              {(types||[]).map(t=><option key={t.id} value={t.id}>{aiProviderLabel(t.id)}</option>)}
            </select>
            {authKind==='aws'&&(
              <div style={{fontSize:12,color:'var(--text3)',marginTop:6}}>
                Requests are signed with AWS SigV4 at the gateway. Use an IAM user scoped to
                {form.provider==='bedrock'?' bedrock:InvokeModel':' comprehend:DetectToxicContent'} only.
              </div>
            )}
            {guardrail&&(
              <div style={{fontSize:12,color:'var(--text3)',marginTop:6}}>
                A guardrail service, not a model: it is chosen in an AI product's Guardrails section.
              </div>
            )}
          </div>

          {authKind!=='key'&&(
            <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
              {authKind==='aws'&&(
                <div className="form-group" style={{flex:'1 1 220px'}}>
                  <label className="form-label">Access key id <span style={{color:'var(--error,#dc2626)'}}>*</span></label>
                  <input className="form-input" value={form.accessKeyId} autoComplete="off"
                         onChange={e=>set('accessKeyId',e.target.value)} placeholder="AKIA…"/>
                </div>
              )}
              {authKind==='gcp'&&(
                <div className="form-group" style={{flex:'1 1 220px'}}>
                  <label className="form-label">GCP project id <span style={{color:'var(--error,#dc2626)'}}>*</span></label>
                  <input className="form-input" value={form.projectId}
                         onChange={e=>set('projectId',e.target.value)} placeholder="my-project"/>
                </div>
              )}
              <div className="form-group" style={{flex:'1 1 160px'}}>
                <label className="form-label">Region <span style={{color:'var(--error,#dc2626)'}}>*</span></label>
                <input className="form-input" value={form.region} onChange={e=>set('region',e.target.value)}
                       placeholder={authKind==='gcp'?'us-central1':'us-east-1'}/>
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">{keyLabel} {isNew?'':<span style={{color:'var(--text3)',fontWeight:400}}>— leave blank to keep the stored value</span>}</label>
            {authKind==='gcp'?(
              <textarea className="form-input" rows={4} autoComplete="off" name="axon-ai-provider-key"
                        ref={keyRef} value={form.apiKey} onChange={e=>set('apiKey',e.target.value)}
                        style={{fontFamily:'monospace',fontSize:12}}
                        placeholder={isNew?'{ "type": "service_account", … }':'••••••••'}/>
            ):(
            <input className="form-input" type="password" autoComplete="off" name="axon-ai-provider-key"
                   ref={keyRef} value={form.apiKey} onChange={e=>set('apiKey',e.target.value)}
                   placeholder={isNew?(authKind==='aws'?'wJalr…':'sk-…'):'••••••••'}/>
            )}
            <div style={{fontSize:12,color:'var(--text3)',marginTop:6}}>
              Stored encrypted. It is never written into gateway configuration and there is no way to
              read it back — rotate it here if it is ever lost.
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Endpoint override {needsEndpoint&&<span style={{color:'var(--error,#dc2626)'}}>*</span>}</label>
            <input className="form-input" value={form.endpointOverride}
                   onChange={e=>set('endpointOverride',e.target.value)}
                   placeholder={needsEndpoint?'https://my-resource.openai.azure.com/…':'Leave blank for the provider default'}/>
          </div>

          {!guardrail&&(<React.Fragment>
          <div className="form-group">
            <label className="form-label">Models</label>
            <input className="form-input" value={form.models} onChange={e=>set('models',e.target.value)}
                   placeholder="gpt-4o, gpt-4o-mini"/>
            {(typeInfo.suggestedModels||[]).length>0&&(
              <div style={{marginTop:8,display:'flex',gap:6,flexWrap:'wrap'}}>
                {typeInfo.suggestedModels.map(m=>(
                  <button key={m} type="button" className="btn btn-sm btn-ghost" style={{fontSize:11,padding:'2px 8px'}}
                          onClick={()=>{
                            const cur=form.models.split(',').map(s=>s.trim()).filter(Boolean);
                            if(cur.indexOf(m)<0)set('models',[...cur,m].join(', '));
                          }}>+ {m}</button>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Default model</label>
            <input className="form-input" value={form.defaultModel} onChange={e=>set('defaultModel',e.target.value)}/>
          </div>

          <div className="form-group">
            <label className="form-label" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <span>List prices</span>
              <button type="button" className="btn btn-sm btn-ghost" onClick={addPrice}>+ Add model price</button>
            </label>
            <div style={{fontSize:12,color:'var(--text3)',marginBottom:8}}>
              Per million tokens, as the provider publishes them. Used to cost usage at the moment it is
              recorded — changing a price here moves future cost, never past records.
            </div>
            {pricing.length===0?<div style={{fontSize:12,color:'var(--text3)'}}>Not priced. Usage will still be metered.</div>:(
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                  <thead>
                    <tr style={{textAlign:'left',color:'var(--text3)',fontSize:11,textTransform:'uppercase'}}>
                      <th style={{padding:'6px 4px'}}>Model</th>
                      <th style={{padding:'6px 4px',width:110}}>Input /1M</th>
                      <th style={{padding:'6px 4px',width:110}}>Output /1M</th>
                      <th style={{padding:'6px 4px',width:120}}>Cached in /1M</th>
                      <th style={{width:36}}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricing.map((row,i)=>(
                      <tr key={i} style={{borderTop:'1px solid var(--border)'}}>
                        <td style={{padding:'4px'}}>
                          <input className="form-input" style={{fontSize:12}} value={row.model||''}
                                 onChange={e=>setPrice(i,'model',e.target.value)}/>
                        </td>
                        <td style={{padding:'4px'}}>
                          <input className="form-input" style={{fontSize:12}} type="number" step="0.01"
                                 value={row.inputPer1M===null||row.inputPer1M===undefined?'':row.inputPer1M}
                                 onChange={e=>setPrice(i,'inputPer1M',e.target.value)}/>
                        </td>
                        <td style={{padding:'4px'}}>
                          <input className="form-input" style={{fontSize:12}} type="number" step="0.01"
                                 value={row.outputPer1M===null||row.outputPer1M===undefined?'':row.outputPer1M}
                                 onChange={e=>setPrice(i,'outputPer1M',e.target.value)}/>
                        </td>
                        <td style={{padding:'4px'}}>
                          <input className="form-input" style={{fontSize:12}} type="number" step="0.01"
                                 value={row.cachedInputPer1M===null||row.cachedInputPer1M===undefined?'':row.cachedInputPer1M}
                                 onChange={e=>setPrice(i,'cachedInputPer1M',e.target.value)}/>
                        </td>
                        <td style={{padding:'4px',textAlign:'center'}}>
                          <button type="button" className="btn btn-sm btn-ghost" onClick={()=>delPrice(i)}>×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </React.Fragment>)}

          <div className="form-group">
            <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13}}>
              <input type="checkbox" checked={form.active} onChange={e=>set('active',e.target.checked)}/>
              Active — deactivating stops the gateway resolving this key, which stops traffic rather
              than merely hiding the row.
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={save}>
            {saving?'Saving…':(isNew?'Create provider':'Save changes')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══ Usage ═══════════════════════════════════════════════════════════════ */

function AiUsagePage({showToast}){
  const [range,setRange]=useState('7d');
  const [groupBy,setGroupBy]=useState('model');
  const [productId,setProductId]=useState('');
  const [products,setProducts]=useState([]);
  const [summary,setSummary]=useState(null);
  const [rows,setRows]=useState([]);
  const [loading,setLoading]=useState(true);

  useEffect(()=>{
    (async()=>{
      try{
        const ps=await API('GET','/api/admin/api-products');
        setProducts((ps||[]).filter(p=>p.type==='AI'));
      }catch(e){/* the filter is a convenience; usage still loads without it */}
    })();
  },[]);

  const load=async()=>{
    setLoading(true);
    try{
      const q='from='+range+(productId?'&productId='+encodeURIComponent(productId):'');
      const [s,r]=await loadAll([
        ['Usage summary',API('GET','/api/admin/ai-usage/summary?groupBy='+groupBy+'&'+q),null],
        ['Usage records',API('GET','/api/admin/ai-usage?limit=100&'+q),[]],
      ],showToast);
      setSummary(s);setRows(r||[]);
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setLoading(false);}
  };
  useEffect(()=>{load();},[range,groupBy,productId]);

  const t=(summary&&summary.totals)||{};
  const exportUrl='/api/admin/ai-usage/export?from='+range+(productId?'&productId='+encodeURIComponent(productId):'');

  return(
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">AI Usage</h1>
          <div className="page-subtitle">
            Token consumption and cost per call, as the provider reported it. One row per request,
            attributed to the subscriber that made it.
          </div>
        </div>
        <a className="btn btn-secondary" href={exportUrl} download>Export CSV</a>
      </div>

      <div className="card" style={{marginBottom:16}}>
        <div className="card-body" style={{display:'flex',gap:16,flexWrap:'wrap',alignItems:'flex-end'}}>
          <div>
            <label className="form-label" style={{fontSize:11}}>Period</label>
            <select className="form-input" value={range} onChange={e=>setRange(e.target.value)}>
              <option value="24h">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </select>
          </div>
          <div>
            <label className="form-label" style={{fontSize:11}}>Product</label>
            <select className="form-input" value={productId} onChange={e=>setProductId(e.target.value)}>
              <option value="">All AI products</option>
              {products.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label" style={{fontSize:11}}>Group by</label>
            <select className="form-input" value={groupBy} onChange={e=>setGroupBy(e.target.value)}>
              <option value="model">Model</option>
              <option value="subscriber">Subscriber</option>
              <option value="product">Product</option>
              <option value="day">Day</option>
              <option value="route">Route</option>
            </select>
          </div>
          <button className="btn btn-sm btn-ghost" onClick={load}>Refresh</button>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:12,marginBottom:16}}>
        <AiStatTile label="Calls" value={aiNum(t.calls)}/>
        <AiStatTile label="Total tokens" value={aiNum(t.totalTokens)}/>
        <AiStatTile label="Prompt" value={aiNum(t.promptTokens)}/>
        <AiStatTile label="Completion" value={aiNum(t.completionTokens)}/>
        <AiStatTile label="Cost" value={aiMoney(t.costUsd)}/>
        <AiStatTile label="Errors" value={aiNum(t.errors)} danger={Number(t.errors||0)>0}/>
      </div>

      <div className="card" style={{marginBottom:16}}>
        <div className="card-header"><div className="card-title">By {groupBy}</div></div>
        <div className="card-body" style={{overflowX:'auto'}}>
          {loading?'Loading…':(summary&&summary.buckets&&summary.buckets.length)?(
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead>
                <tr style={{textAlign:'left',color:'var(--text3)',fontSize:11,textTransform:'uppercase'}}>
                  <th style={{padding:'8px 6px'}}>{groupBy}</th>
                  <th style={{padding:'8px 6px',textAlign:'right'}}>Calls</th>
                  <th style={{padding:'8px 6px',textAlign:'right'}}>Prompt</th>
                  <th style={{padding:'8px 6px',textAlign:'right'}}>Completion</th>
                  <th style={{padding:'8px 6px',textAlign:'right'}}>Total</th>
                  <th style={{padding:'8px 6px',textAlign:'right'}}>Cost</th>
                  <th style={{padding:'8px 6px',textAlign:'right'}}>Errors</th>
                </tr>
              </thead>
              <tbody>
                {summary.buckets.map((b,i)=>(
                  <tr key={i} style={{borderTop:'1px solid var(--border)'}}>
                    <td style={{padding:'8px 6px',fontFamily:'monospace',fontSize:12}}>{b.bucket||'—'}</td>
                    <td style={{padding:'8px 6px',textAlign:'right'}}>{aiNum(b.calls)}</td>
                    <td style={{padding:'8px 6px',textAlign:'right'}}>{aiNum(b.promptTokens)}</td>
                    <td style={{padding:'8px 6px',textAlign:'right'}}>{aiNum(b.completionTokens)}</td>
                    <td style={{padding:'8px 6px',textAlign:'right'}}>{aiNum(b.totalTokens)}</td>
                    <td style={{padding:'8px 6px',textAlign:'right'}}>{aiMoney(b.costUsd)}</td>
                    <td style={{padding:'8px 6px',textAlign:'right'}}>{aiNum(b.errors)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ):<div style={{color:'var(--text3)',fontSize:13}}>No usage recorded in this period.</div>}
        </div>
      </div>

      <div className="card">
        <div className="card-header"><div className="card-title">Recent calls</div></div>
        <div className="card-body" style={{overflowX:'auto'}}>
          {rows.length===0?<div style={{color:'var(--text3)',fontSize:13}}>Nothing to show.</div>:(
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr style={{textAlign:'left',color:'var(--text3)',fontSize:11,textTransform:'uppercase'}}>
                  <th style={{padding:'8px 6px'}}>When</th>
                  <th style={{padding:'8px 6px'}}>Subscriber</th>
                  <th style={{padding:'8px 6px'}}>Model</th>
                  <th style={{padding:'8px 6px',textAlign:'right'}}>Tokens</th>
                  <th style={{padding:'8px 6px',textAlign:'right'}}>Cost</th>
                  <th style={{padding:'8px 6px',textAlign:'right'}}>TTFT</th>
                  <th style={{padding:'8px 6px',textAlign:'right'}}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r=>(
                  <tr key={r.id} style={{borderTop:'1px solid var(--border)'}}>
                    <td style={{padding:'8px 6px',whiteSpace:'nowrap'}}>{new Date(r.timestamp).toLocaleString()}</td>
                    <td style={{padding:'8px 6px',fontFamily:'monospace'}}>{r.clientId||'—'}</td>
                    <td style={{padding:'8px 6px'}}>{r.model||r.requestedModel||'—'}</td>
                    <td style={{padding:'8px 6px',textAlign:'right'}}>
                      {aiNum(r.totalTokens)}
                      <span style={{color:'var(--text3)'}}> ({aiNum(r.promptTokens)}/{aiNum(r.completionTokens)})</span>
                    </td>
                    <td style={{padding:'8px 6px',textAlign:'right'}}>{r.costUsd?aiMoney(r.costUsd):'—'}</td>
                    <td style={{padding:'8px 6px',textAlign:'right'}}>{r.ttftMs?r.ttftMs+'ms':'—'}</td>
                    <td style={{padding:'8px 6px',textAlign:'right'}}>
                      <span className={'badge badge-'+(r.httpStatus>=400?'error':'success')} style={{fontSize:10}}>
                        {r.httpStatus}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function AiStatTile({label,value,danger}){
  return(
    <div className="card"><div className="card-body" style={{padding:'14px 16px'}}>
      <div style={{fontSize:11,color:'var(--text3)',textTransform:'uppercase',letterSpacing:.4}}>{label}</div>
      <div style={{fontSize:20,fontWeight:600,marginTop:4,color:danger?'var(--error,#dc2626)':'inherit'}}>{value}</div>
    </div></div>
  );
}

/* ══ AI configuration, inside the product panel ══════════════════════════ */

function ProductAiTab({product,onChanged,showToast}){
  const cfg=product.aiConfig||{};
  const [providers,setProviders]=useState([]);
  const [instances,setInstances]=useState(cfg.instances||[]);
  const [allowedModels,setAllowedModels]=useState((cfg.allowedModels||[]).join(', '));
  const [pinModel,setPinModel]=useState(!!cfg.pinModel);
  const [tokenQuota,setTokenQuota]=useState(cfg.tokenQuota||{enabled:false,limit:100000,timeWindowSeconds:86400,strategy:'total_tokens',perSubscriber:true});
  const [guardrails,setGuardrails]=useState(cfg.guardrails||{enabled:false,denyPatterns:[],allowPatterns:[],matchAllRoles:false});
  const [lakera,setLakera]=useState((cfg.guardrails&&cfg.guardrails.lakera)||{enabled:false,providerId:'',projectId:'',direction:'input',action:'block',failOpen:false,denyCode:null});
  const [moderation,setModeration]=useState((cfg.guardrails&&cfg.guardrails.moderation)||{enabled:false,providerId:'',threshold:0.5,categories:{},checkRequest:true,checkResponse:false});
  const [redisFailOpen,setRedisFailOpen]=useState(cfg.redisFailOpen===true?'open':cfg.redisFailOpen===false?'closed':'auto');
  const [cache,setCache]=useState(cfg.cache||{enabled:false,ttlSeconds:3600,perSubscriber:true,allowBypass:true,semantic:false,similarityThreshold:0.95,semanticTtlSeconds:86400,embeddingProviderId:'',embeddingModel:'text-embedding-3-small'});
  const [decoration,setDecoration]=useState(cfg.decoration||{enabled:false,prepend:[],append:[]});
  const [balancer,setBalancer]=useState(cfg.balancerAlgorithm||'roundrobin');
  const [fallback,setFallback]=useState(cfg.fallbackStrategy||[]);
  const [saving,setSaving]=useState(false);
  const [syncing,setSyncing]=useState(false);
  const [syncResult,setSyncResult]=useState(null);

  useEffect(()=>{
    (async()=>{
      try{setProviders(await API('GET','/api/admin/ai-providers')||[]);}
      catch(e){/* the picker degrades to free text without this */}
    })();
  },[]);

  const llmProviders=providers.filter(p=>!aiIsGuardrail(p.provider));
  const providersOf=(kinds)=>providers.filter(p=>kinds.indexOf(p.provider)>=0);
  const addInstance=()=>setInstances(x=>[...x,{name:'',aiProviderId:llmProviders[0]?llmProviders[0].id:'',model:'',weight:1,priority:0,active:true}]);
  const setInst=(i,k,v)=>setInstances(x=>x.map((r,j)=>j===i?{...r,[k]:v}:r));
  const delInst=(i)=>setInstances(x=>x.filter((_,j)=>j!==i));

  const save=async()=>{
    if(instances.length===0)return showToast&&showToast('Add at least one model instance','error');
    setSaving(true);
    try{
      // The PUT replaces the whole config, so start from what is stored and overlay what
      // this tab edits. Building the body from only the fields shown here dropped every
      // setting the tab has no control for (hash key, retries, response caps, payload
      // logging) on each save.
      const body={
        ...cfg,
        instances:instances.map(i=>({...i,weight:Number(i.weight)||1,priority:Number(i.priority)||0})),
        allowedModels:allowedModels.split(',').map(s=>s.trim()).filter(Boolean),
        pinModel,
        balancerAlgorithm:balancer,
        fallbackStrategy:fallback,
        tokenQuota:{...tokenQuota,limit:Number(tokenQuota.limit)||0,timeWindowSeconds:Number(tokenQuota.timeWindowSeconds)||0,
                    budgetUsd:tokenQuota.strategy==='cost'?(Number(tokenQuota.budgetUsd)||0):(tokenQuota.budgetUsd||null)},
        guardrails:{...guardrails,
                    lakera:{...lakera,denyCode:lakera.denyCode?Number(lakera.denyCode):null},
                    moderation:{...moderation,threshold:Number(moderation.threshold)}},
        redisFailOpen:redisFailOpen==='auto'?null:redisFailOpen==='open',
        cache:{...cache,ttlSeconds:Number(cache.ttlSeconds)||3600,
               similarityThreshold:Number(cache.similarityThreshold)||0.95,
               semanticTtlSeconds:Number(cache.semanticTtlSeconds)||86400},
        decoration,
        templates:cfg.templates||[],
        logSummaries:cfg.logSummaries!==false,
        sslVerify:cfg.sslVerify!==false,
        timeoutMs:cfg.timeoutMs||60000
      };
      await API('PUT','/api/admin/api-products/'+product.id+'/ai-config',body);
      showToast&&showToast('AI configuration saved — publish it with Sync to gateway','success');
      onChanged&&onChanged();
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setSaving(false);}
  };

  const sync=async()=>{
    setSyncing(true);setSyncResult(null);
    try{
      const r=await API('POST','/api/admin/api-products/'+product.id+'/sync-apisix');
      setSyncResult(r);
      showToast&&showToast(r.ok?'Published to the gateway':'Publish reported errors',r.ok?'success':'error');
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setSyncing(false);}
  };

  const patterns=(list)=>(list||[]).join('\n');
  const setPatterns=(v)=>v.split('\n').map(s=>s.trim()).filter(Boolean);

  return(
    <div style={{display:'grid',gap:16}}>
      <div className="card">
        <div className="card-header" style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div className="card-title">Models</div>
          <button className="btn btn-sm btn-ghost" onClick={addInstance}>+ Add instance</button>
        </div>
        <div className="card-body">
          <div style={{fontSize:12,color:'var(--text3)',marginBottom:10}}>
            Each instance binds a provider to a model. One instance publishes a single upstream; two or
            more publish a balanced pool with fallback, so a provider outage does not take the product down.
          </div>
          {instances.length===0?<div style={{fontSize:13,color:'var(--text3)'}}>No instances yet.</div>:(
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                <thead>
                  <tr style={{textAlign:'left',color:'var(--text3)',fontSize:11,textTransform:'uppercase'}}>
                    <th style={{padding:'6px 4px',width:130}}>Name</th>
                    <th style={{padding:'6px 4px'}}>Provider</th>
                    <th style={{padding:'6px 4px'}}>Model</th>
                    <th style={{padding:'6px 4px',width:70}}>Weight</th>
                    <th style={{width:36}}></th>
                  </tr>
                </thead>
                <tbody>
                  {instances.map((inst,i)=>(
                    <tr key={i} style={{borderTop:'1px solid var(--border)'}}>
                      <td style={{padding:'4px'}}>
                        <input className="form-input" style={{fontSize:12}} value={inst.name||''}
                               placeholder="primary" onChange={e=>setInst(i,'name',e.target.value)}/>
                      </td>
                      <td style={{padding:'4px'}}>
                        <select className="form-input" style={{fontSize:12}} value={inst.aiProviderId||''}
                                onChange={e=>setInst(i,'aiProviderId',e.target.value)}>
                          <option value="">Select…</option>
                          {llmProviders.map(p=><option key={p.id} value={p.id}>{p.name} ({aiProviderLabel(p.provider)})</option>)}
                        </select>
                      </td>
                      <td style={{padding:'4px'}}>
                        <input className="form-input" style={{fontSize:12}} value={inst.model||''}
                               placeholder="gpt-4o-mini" onChange={e=>setInst(i,'model',e.target.value)}/>
                      </td>
                      <td style={{padding:'4px'}}>
                        <input className="form-input" style={{fontSize:12}} type="number" min="0"
                               value={inst.weight===undefined?1:inst.weight}
                               onChange={e=>setInst(i,'weight',e.target.value)}/>
                      </td>
                      <td style={{padding:'4px',textAlign:'center'}}>
                        <button className="btn btn-sm btn-ghost" onClick={()=>delInst(i)}>×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {instances.length>1&&(
            <div style={{display:'flex',gap:16,marginTop:14,flexWrap:'wrap'}}>
              <div>
                <label className="form-label" style={{fontSize:11}}>Balancer</label>
                <select className="form-input" value={balancer} onChange={e=>setBalancer(e.target.value)}>
                  <option value="roundrobin">Round robin</option>
                  <option value="chash">Consistent hash</option>
                </select>
              </div>
              <div>
                <label className="form-label" style={{fontSize:11}}>Fall back on</label>
                <div style={{display:'flex',gap:10,alignItems:'center',height:36}}>
                  {['rate_limiting','http_429','http_5xx'].map(f=>(
                    <label key={f} style={{display:'flex',alignItems:'center',gap:5,fontSize:12}}>
                      <input type="checkbox" checked={fallback.indexOf(f)>=0}
                             onChange={e=>setFallback(x=>e.target.checked?[...x,f]:x.filter(y=>y!==f))}/>
                      {f.replace(/_/g,' ')}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header"><div className="card-title">Model policy</div></div>
        <div className="card-body">
          <div className="form-group">
            <label className="form-label">Allowed models</label>
            <input className="form-input" value={allowedModels} onChange={e=>setAllowedModels(e.target.value)}
                   placeholder="gpt-4o-mini, gpt-4o"/>
            <div style={{fontSize:12,color:'var(--text3)',marginTop:6}}>
              What a caller may ask for. Leave empty and any model the provider offers is reachable —
              including ones far more expensive than this plan was priced for.
            </div>
          </div>
          <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13}}>
            <input type="checkbox" checked={pinModel} onChange={e=>setPinModel(e.target.checked)}/>
            Pin the model — override whatever the caller asks for with the instance's model
          </label>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><div className="card-title">Token &amp; spend budget</div></div>
        <div className="card-body">
          <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13,marginBottom:12}}>
            <input type="checkbox" checked={!!tokenQuota.enabled}
                   onChange={e=>setTokenQuota(q=>({...q,enabled:e.target.checked}))}/>
            Enforce a budget
          </label>
          {tokenQuota.enabled&&(
            <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
              {tokenQuota.strategy==='cost'?(
                <div>
                  <label className="form-label" style={{fontSize:11}}>Budget (USD)</label>
                  <input className="form-input" type="number" step="0.01" min="0" value={tokenQuota.budgetUsd||''}
                         onChange={e=>setTokenQuota(q=>({...q,budgetUsd:e.target.value}))} placeholder="50.00"/>
                </div>
              ):(
              <div>
                <label className="form-label" style={{fontSize:11}}>Tokens</label>
                <input className="form-input" type="number" value={tokenQuota.limit}
                       onChange={e=>setTokenQuota(q=>({...q,limit:e.target.value}))}/>
              </div>
              )}
              <div>
                <label className="form-label" style={{fontSize:11}}>Per (seconds)</label>
                <input className="form-input" type="number" value={tokenQuota.timeWindowSeconds}
                       onChange={e=>setTokenQuota(q=>({...q,timeWindowSeconds:e.target.value}))}/>
              </div>
              <div>
                <label className="form-label" style={{fontSize:11}}>Counting</label>
                <select className="form-input" value={tokenQuota.strategy}
                        onChange={e=>setTokenQuota(q=>({...q,strategy:e.target.value}))}>
                  <option value="total_tokens">Total tokens</option>
                  <option value="prompt_tokens">Prompt tokens</option>
                  <option value="completion_tokens">Completion tokens</option>
                  <option value="cost">Spend (USD)</option>
                </select>
              </div>
              <div style={{display:'flex',alignItems:'flex-end'}}>
                <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12,height:36}}>
                  <input type="checkbox" checked={tokenQuota.perSubscriber!==false}
                         onChange={e=>setTokenQuota(q=>({...q,perSubscriber:e.target.checked}))}/>
                  Per subscriber
                </label>
              </div>
            </div>
          )}
          <div style={{display:'flex',alignItems:'center',gap:10,marginTop:12,flexWrap:'wrap'}}>
            <label className="form-label" style={{fontSize:11,margin:0}}>If the gateway's Redis is unreachable</label>
            <select className="form-input" style={{width:'auto'}} value={redisFailOpen}
                    onChange={e=>setRedisFailOpen(e.target.value)}>
              <option value="auto">Automatic — refuse calls when a spend budget is on, else let them through</option>
              <option value="closed">Refuse calls (never spend unmetered)</option>
              <option value="open">Let calls through uncounted</option>
            </select>
          </div>
          <div style={{fontSize:12,color:'var(--text3)',marginTop:10}}>
            {tokenQuota.strategy==='cost'
              ?'A hard spend cap, charged at the gateway from each call\'s reported usage and the providers\' list prices. It applies the most expensive price among the models this product serves, so every model must be priced and reachable models must be pinned or allow-listed. Per-subscriber token overrides do not apply to a spend budget.'
              :'This is the product default. An individual subscriber can be given their own budget on the Subscriptions tab, which takes effect without republishing the route.'}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><div className="card-title">Guardrails</div></div>
        <div className="card-body">
          <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13,marginBottom:12}}>
            <input type="checkbox" checked={!!guardrails.enabled}
                   onChange={e=>setGuardrails(g=>({...g,enabled:e.target.checked}))}/>
            Screen prompts before they leave the gateway
          </label>
          {guardrails.enabled&&(
            <div style={{display:'grid',gap:12}}>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">Deny patterns — one regex per line; a match is rejected</label>
                <textarea className="form-input" rows={3} value={patterns(guardrails.denyPatterns)}
                          onChange={e=>setGuardrails(g=>({...g,denyPatterns:setPatterns(e.target.value)}))}
                          placeholder="(?i)ignore previous instructions"/>
              </div>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">Allow patterns — if any are set, a prompt matching none is rejected</label>
                <textarea className="form-input" rows={2} value={patterns(guardrails.allowPatterns)}
                          onChange={e=>setGuardrails(g=>({...g,allowPatterns:setPatterns(e.target.value)}))}/>
              </div>
              <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12}}>
                <input type="checkbox" checked={!!guardrails.matchAllRoles}
                       onChange={e=>setGuardrails(g=>({...g,matchAllRoles:e.target.checked}))}/>
                Inspect system and assistant turns too, not just the user's
              </label>
              <div style={{fontSize:12,color:'var(--text3)'}}>
                A pattern filter, not a jailbreak defence. It buys policy at no latency; it does not buy
                semantic safety — the services below do.
              </div>
            </div>
          )}

          <div style={{borderTop:'1px solid var(--border)',margin:'16px 0 12px'}}/>
          <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13,marginBottom:10}}>
            <input type="checkbox" checked={!!lakera.enabled}
                   onChange={e=>setLakera(l=>({...l,enabled:e.target.checked}))}/>
            Lakera Guard — prompt injection, jailbreaks, PII and unsafe content
          </label>
          {lakera.enabled&&(
            <div style={{display:'flex',gap:12,flexWrap:'wrap',marginBottom:6}}>
              <div style={{flex:'1 1 200px'}}>
                <label className="form-label" style={{fontSize:11}}>Account</label>
                <select className="form-input" value={lakera.providerId||''}
                        onChange={e=>setLakera(l=>({...l,providerId:e.target.value}))}>
                  <option value="">Select…</option>
                  {providersOf(['lakera']).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div style={{flex:'1 1 160px'}}>
                <label className="form-label" style={{fontSize:11}}>Lakera project (policy)</label>
                <input className="form-input" value={lakera.projectId||''} placeholder="account default"
                       onChange={e=>setLakera(l=>({...l,projectId:e.target.value}))}/>
              </div>
              <div>
                <label className="form-label" style={{fontSize:11}}>Screen</label>
                <select className="form-input" value={lakera.direction||'input'}
                        onChange={e=>setLakera(l=>({...l,direction:e.target.value}))}>
                  <option value="input">Prompts</option>
                  <option value="output">Completions</option>
                  <option value="both">Both</option>
                </select>
              </div>
              <div>
                <label className="form-label" style={{fontSize:11}}>On a hit</label>
                <select className="form-input" value={lakera.action||'block'}
                        onChange={e=>setLakera(l=>({...l,action:e.target.value}))}>
                  <option value="block">Block</option>
                  <option value="alert">Log only (shadow)</option>
                </select>
              </div>
              <div style={{width:110}}>
                <label className="form-label" style={{fontSize:11}}>Block status</label>
                <input className="form-input" type="number" placeholder="200" value={lakera.denyCode||''}
                       onChange={e=>setLakera(l=>({...l,denyCode:e.target.value}))}/>
              </div>
              <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,flexBasis:'100%'}}>
                <input type="checkbox" checked={!!lakera.failOpen}
                       onChange={e=>setLakera(l=>({...l,failOpen:e.target.checked}))}/>
                Let traffic through when Lakera is unreachable (default: refuse it)
              </label>
              {providersOf(['lakera']).length===0&&(
                <div style={{fontSize:12,color:'var(--warning,#b45309)',flexBasis:'100%'}}>
                  No Lakera account yet — add one under AI Gateway → Providers.
                </div>
              )}
            </div>
          )}

          <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13,margin:'10px 0'}}>
            <input type="checkbox" checked={!!moderation.enabled}
                   onChange={e=>setModeration(m=>({...m,enabled:e.target.checked}))}/>
            AWS Comprehend — block toxic content
          </label>
          {moderation.enabled&&(
            <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
              <div style={{flex:'1 1 200px'}}>
                <label className="form-label" style={{fontSize:11}}>Account</label>
                <select className="form-input" value={moderation.providerId||''}
                        onChange={e=>setModeration(m=>({...m,providerId:e.target.value}))}>
                  <option value="">Select…</option>
                  {providersOf(['aws-comprehend']).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div style={{width:120}}>
                <label className="form-label" style={{fontSize:11}}>Threshold (0–1)</label>
                <input className="form-input" type="number" step="0.05" min="0" max="1" value={moderation.threshold}
                       onChange={e=>setModeration(m=>({...m,threshold:e.target.value}))}/>
              </div>
              <div style={{display:'flex',alignItems:'flex-end',gap:14,height:58}}>
                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12}}>
                  <input type="checkbox" checked={moderation.checkRequest!==false}
                         onChange={e=>setModeration(m=>({...m,checkRequest:e.target.checked}))}/>
                  Prompts
                </label>
                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12}}>
                  <input type="checkbox" checked={!!moderation.checkResponse}
                         onChange={e=>setModeration(m=>({...m,checkResponse:e.target.checked}))}/>
                  Completions
                </label>
              </div>
              {providersOf(['aws-comprehend']).length===0&&(
                <div style={{fontSize:12,color:'var(--warning,#b45309)',flexBasis:'100%'}}>
                  No AWS Comprehend account yet — add one under AI Gateway → Providers.
                </div>
              )}
            </div>
          )}
          <div style={{fontSize:12,color:'var(--text3)',marginTop:10}}>
            Screening blocks; it does not rewrite. A prompt carrying PII is refused at the gateway rather
            than redacted and forwarded.
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><div className="card-title">Response cache</div></div>
        <div className="card-body">
          <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13,marginBottom:12}}>
            <input type="checkbox" checked={!!cache.enabled}
                   onChange={e=>setCache(c=>({...c,enabled:e.target.checked}))}/>
            Serve repeated prompts from cache
          </label>
          {cache.enabled&&(
            <div style={{display:'grid',gap:12}}>
              <div style={{display:'flex',gap:16,flexWrap:'wrap',alignItems:'flex-end'}}>
                <div style={{width:140}}>
                  <label className="form-label" style={{fontSize:11}}>Keep for (seconds)</label>
                  <input className="form-input" type="number" min="1" value={cache.ttlSeconds}
                         onChange={e=>setCache(c=>({...c,ttlSeconds:e.target.value}))}/>
                </div>
                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,height:36}}>
                  <input type="checkbox" checked={cache.perSubscriber!==false}
                         onChange={e=>setCache(c=>({...c,perSubscriber:e.target.checked,
                                                     semantic:e.target.checked?c.semantic:false}))}/>
                  Separate cache per subscriber
                </label>
                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,height:36}}>
                  <input type="checkbox" checked={cache.allowBypass!==false}
                         onChange={e=>setCache(c=>({...c,allowBypass:e.target.checked}))}/>
                  Honour <code>X-AI-Cache-Bypass: 1</code>
                </label>
              </div>
              <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12}}>
                <input type="checkbox" checked={!!cache.semantic} disabled={cache.perSubscriber===false}
                       onChange={e=>setCache(c=>({...c,semantic:e.target.checked}))}/>
                Also match similar prompts (semantic, via embeddings)
              </label>
              {cache.semantic&&(
                <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
                  <div style={{flex:'1 1 200px'}}>
                    <label className="form-label" style={{fontSize:11}}>Embedding account</label>
                    <select className="form-input" value={cache.embeddingProviderId||''}
                            onChange={e=>setCache(c=>({...c,embeddingProviderId:e.target.value}))}>
                      <option value="">Select…</option>
                      {providersOf(['openai','azure-openai']).map(p=><option key={p.id} value={p.id}>{p.name} ({aiProviderLabel(p.provider)})</option>)}
                    </select>
                  </div>
                  <div style={{flex:'1 1 180px'}}>
                    <label className="form-label" style={{fontSize:11}}>Embedding model</label>
                    <input className="form-input" value={cache.embeddingModel||''}
                           onChange={e=>setCache(c=>({...c,embeddingModel:e.target.value}))}/>
                  </div>
                  <div style={{width:130}}>
                    <label className="form-label" style={{fontSize:11}}>Similarity (0–1)</label>
                    <input className="form-input" type="number" step="0.01" min="0" max="1" value={cache.similarityThreshold}
                           onChange={e=>setCache(c=>({...c,similarityThreshold:e.target.value}))}/>
                  </div>
                </div>
              )}
              <div style={{fontSize:12,color:'var(--text3)'}}>
                Needs Redis on the gateway environment (Gateway Environments); on one without it the cache
                is skipped and the publish result says so. Per-subscriber scope is what stops one customer
                being answered with another's completion — the semantic layer requires it.
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header"><div className="card-title">House prompt</div></div>
        <div className="card-body">
          <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13,marginBottom:12}}>
            <input type="checkbox" checked={!!decoration.enabled}
                   onChange={e=>setDecoration(d=>({...d,enabled:e.target.checked}))}/>
            Prepend a system message to every prompt
          </label>
          {decoration.enabled&&(
            <div className="form-group" style={{margin:0}}>
              <textarea className="form-input" rows={3}
                        value={(decoration.prepend&&decoration.prepend[0]&&decoration.prepend[0].content)||''}
                        onChange={e=>setDecoration(d=>({...d,prepend:[{role:'system',content:e.target.value}]}))}
                        placeholder="You are the Axon assistant. Answer only questions about e-invoicing."/>
              <div style={{fontSize:12,color:'var(--text3)',marginTop:6}}>
                Applied at the gateway, so it survives whatever the customer's client sends. This is
                policy, not a default.
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
        <button className="btn btn-primary" disabled={saving} onClick={save}>
          {saving?'Saving…':'Save configuration'}
        </button>
        <button className="btn btn-secondary" disabled={syncing} onClick={sync}>
          {syncing?'Publishing…':'Sync to gateway'}
        </button>
        <span style={{fontSize:12,color:'var(--text3)'}}>
          Saving stores the configuration; syncing publishes it to APISIX.
        </span>
      </div>

      {syncResult&&(
        <div className="card">
          <div className="card-header"><div className="card-title">
            Publish result {syncResult.ok?<span className="badge badge-success" style={{fontSize:10}}>OK</span>
                                        :<span className="badge badge-error" style={{fontSize:10}}>Errors</span>}
          </div></div>
          <div className="card-body" style={{fontSize:12,fontFamily:'monospace'}}>
            {(syncResult.applied||[]).map((a,i)=><div key={'a'+i} style={{color:'var(--success,#16a34a)'}}>✓ {a}</div>)}
            {(syncResult.skipped||[]).map((s,i)=><div key={'s'+i} style={{color:'var(--text3)'}}>– {s}</div>)}
            {(syncResult.errors||[]).map((e,i)=><div key={'e'+i} style={{color:'var(--error,#dc2626)'}}>✗ {e}</div>)}
          </div>
        </div>
      )}
    </div>
  );
}

function ProductAiUsageTab({product,showToast}){
  const [summary,setSummary]=useState(null);
  const [range,setRange]=useState('7d');
  const [loading,setLoading]=useState(true);

  useEffect(()=>{
    let cancelled=false;
    (async()=>{
      setLoading(true);
      try{
        const s=await API('GET','/api/admin/ai-usage/summary?groupBy=subscriber&from='+range
                             +'&productId='+encodeURIComponent(product.id));
        if(!cancelled)setSummary(s);
      }catch(e){showToast&&showToast(e.message,'error');}
      finally{if(!cancelled)setLoading(false);}
    })();
    return()=>{cancelled=true;};
  },[product.id,range]);

  const t=(summary&&summary.totals)||{};
  return(
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12,gap:12,flexWrap:'wrap'}}>
        <div style={{fontSize:13,color:'var(--text3)'}}>Consumption by subscriber, as the provider reported it.</div>
        <select className="form-input" style={{width:160}} value={range} onChange={e=>setRange(e.target.value)}>
          <option value="24h">Last 24 hours</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
        </select>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:12,marginBottom:16}}>
        <AiStatTile label="Calls" value={aiNum(t.calls)}/>
        <AiStatTile label="Total tokens" value={aiNum(t.totalTokens)}/>
        <AiStatTile label="Cost" value={aiMoney(t.costUsd)}/>
        <AiStatTile label="Errors" value={aiNum(t.errors)} danger={Number(t.errors||0)>0}/>
      </div>
      {loading?'Loading…':(summary&&summary.buckets&&summary.buckets.length)?(
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead>
              <tr style={{textAlign:'left',color:'var(--text3)',fontSize:11,textTransform:'uppercase'}}>
                <th style={{padding:'8px 6px'}}>Subscriber</th>
                <th style={{padding:'8px 6px',textAlign:'right'}}>Calls</th>
                <th style={{padding:'8px 6px',textAlign:'right'}}>Tokens</th>
                <th style={{padding:'8px 6px',textAlign:'right'}}>Cost</th>
                <th style={{padding:'8px 6px',textAlign:'right'}}>Errors</th>
              </tr>
            </thead>
            <tbody>
              {summary.buckets.map((b,i)=>(
                <tr key={i} style={{borderTop:'1px solid var(--border)'}}>
                  <td style={{padding:'8px 6px',fontFamily:'monospace',fontSize:12}}>{b.bucket||'—'}</td>
                  <td style={{padding:'8px 6px',textAlign:'right'}}>{aiNum(b.calls)}</td>
                  <td style={{padding:'8px 6px',textAlign:'right'}}>{aiNum(b.totalTokens)}</td>
                  <td style={{padding:'8px 6px',textAlign:'right'}}>{aiMoney(b.costUsd)}</td>
                  <td style={{padding:'8px 6px',textAlign:'right'}}>{aiNum(b.errors)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ):<div style={{color:'var(--text3)',fontSize:13}}>No usage recorded in this period.</div>}
    </div>
  );
}

/* ══ Portal: model catalogue and playground ══════════════════════════════ */

function PortalAiPanel({product,subscription}){
  const [tab,setTab]=useState('models');
  return(
    <div>
      <div className="um-tabs" style={{marginBottom:16}}>
        {[['models','Models'],['playground','Playground'],['usage','Your usage']].map(([id,label])=>(
          <button key={id} className={'um-tab'+(tab===id?' active':'')} onClick={()=>setTab(id)}>{label}</button>
        ))}
      </div>
      {tab==='models'&&<PortalAiModels product={product}/>}
      {tab==='playground'&&<PortalAiPlayground product={product} subscription={subscription}/>}
      {tab==='usage'&&<PortalAiUsage subscription={subscription}/>}
    </div>
  );
}

function PortalAiModels({product}){
  const models=product.models||[];
  return(
    <div className="card"><div className="card-body">
      <div style={{fontSize:13,color:'var(--text2)',marginBottom:12}}>
        Models available on this product. Send the model name in the <code>model</code> field of your
        request; anything not listed here is refused at the gateway.
      </div>
      {models.length===0?<div style={{color:'var(--text3)',fontSize:13}}>No models published.</div>:(
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {models.map(m=>(
            <span key={m} className="badge badge-gray"
                  style={{fontSize:12,fontFamily:'monospace',padding:'6px 10px'}}>{m}</span>
          ))}
        </div>
      )}
      <div style={{marginTop:16,fontSize:13}}>
        <div style={{color:'var(--text3)',fontSize:11,textTransform:'uppercase',letterSpacing:.4,marginBottom:4}}>
          Token allowance
        </div>
        {aiTokenQuotaLabel(product.tokenQuota)}
      </div>
      {(product.promptTemplates||[]).length>0&&(
        <div style={{marginTop:16}}>
          <div style={{color:'var(--text3)',fontSize:11,textTransform:'uppercase',letterSpacing:.4,marginBottom:6}}>
            Prompt templates
          </div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <tbody>
              {product.promptTemplates.map((t,i)=>(
                <tr key={i} style={{borderTop:'1px solid var(--border)'}}>
                  <td style={{padding:'6px 4px',fontFamily:'monospace',width:180}}>{t.name}</td>
                  <td style={{padding:'6px 4px',color:'var(--text2)'}}>{t.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div></div>
  );
}

function PortalAiPlayground({product,subscription}){
  const chatPath=(product.operations||[]).filter(o=>!o.authEndpoint)[0];
  const [clientId,setClientId]=useState((subscription&&subscription.axonClientId)||'');
  const [clientSecret,setClientSecret]=useState('');
  const [token,setToken]=useState('');
  const [model,setModel]=useState((product.models||[])[0]||'');
  const [prompt,setPrompt]=useState('Summarise what this API product does in one sentence.');
  const [busy,setBusy]=useState(false);
  const [result,setResult]=useState(null);
  const [error,setError]=useState(null);

  const getToken=async()=>{
    setBusy(true);setError(null);
    try{
      // Raw fetch, not API(): this exchanges the CUSTOMER's credentials, and API()
      // would attach the operator's session token instead.
      const r=await fetch(product.tokenPath||'/api/external-auth/token',{
        method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({grant_type:'client_credentials',client_id:clientId,client_secret:clientSecret})
      });
      const d=await r.json();
      if(!r.ok)throw new Error(d.error_description||d.error||('HTTP '+r.status));
      setToken(d.access_token||'');
      // Held in component state only. This is a customer credential, not the
      // operator's session, and it has no business in localStorage.
      setClientSecret('');
    }catch(e){setError(e.message);}
    finally{setBusy(false);}
  };

  const send=async()=>{
    if(!token)return setError('Get a token first.');
    if(!chatPath)return setError('This product publishes no callable operation.');
    setBusy(true);setError(null);setResult(null);
    try{
      const r=await fetch(chatPath.path,{
        method:'POST',
        headers:{'Content-Type':'application/json','Authorization':'Bearer '+token},
        body:JSON.stringify({model,messages:[{role:'user',content:prompt}]})
      });
      const text=await r.text();
      let body;try{body=JSON.parse(text);}catch(e){body=text;}
      setResult({status:r.status,body});
    }catch(e){setError(e.message);}
    finally{setBusy(false);}
  };

  const reply=(()=>{
    const b=result&&result.body;
    if(!b||typeof b!=='object')return null;
    const c=b.choices&&b.choices[0];
    return (c&&c.message&&c.message.content)||null;
  })();
  const usage=(result&&result.body&&result.body.usage)||null;

  return(
    <div style={{display:'grid',gap:16}}>
      <div className="card">
        <div className="card-header"><div className="card-title">1 · Get a token</div></div>
        <div className="card-body">
          <div style={{display:'flex',gap:12,flexWrap:'wrap',alignItems:'flex-end'}}>
            <div style={{flex:'1 1 220px'}}>
              <label className="form-label" style={{fontSize:11}}>Client ID</label>
              <input className="form-input" value={clientId} onChange={e=>setClientId(e.target.value)}/>
            </div>
            <div style={{flex:'1 1 220px'}}>
              <label className="form-label" style={{fontSize:11}}>Client secret</label>
              <input className="form-input" type="password" value={clientSecret}
                     onChange={e=>setClientSecret(e.target.value)} placeholder="Shown once, when issued"/>
            </div>
            <button className="btn btn-secondary" disabled={busy||!clientId||!clientSecret} onClick={getToken}>
              {busy?'…':'Get token'}
            </button>
          </div>
          {token&&<div style={{marginTop:10,fontSize:12,color:'var(--success,#16a34a)'}}>
            Token acquired — held in this page only, never stored.
          </div>}
        </div>
      </div>

      <div className="card">
        <div className="card-header"><div className="card-title">2 · Send a prompt</div></div>
        <div className="card-body">
          <div style={{display:'flex',gap:12,flexWrap:'wrap',marginBottom:12}}>
            <div style={{flex:'0 0 240px'}}>
              <label className="form-label" style={{fontSize:11}}>Model</label>
              {(product.models||[]).length>0?(
                <select className="form-input" value={model} onChange={e=>setModel(e.target.value)}>
                  {product.models.map(m=><option key={m} value={m}>{m}</option>)}
                </select>
              ):<input className="form-input" value={model} onChange={e=>setModel(e.target.value)}/>}
            </div>
            <div style={{flex:'1 1 200px'}}>
              <label className="form-label" style={{fontSize:11}}>Endpoint</label>
              <input className="form-input" readOnly value={chatPath?chatPath.path:'—'}
                     style={{fontFamily:'monospace',fontSize:12}}/>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Prompt</label>
            <textarea className="form-input" rows={4} value={prompt} onChange={e=>setPrompt(e.target.value)}/>
          </div>
          <button className="btn btn-primary" disabled={busy||!token} onClick={send}>
            {busy?'Sending…':'Send'}
          </button>
        </div>
      </div>

      {error&&<div className="card"><div className="card-body" style={{color:'var(--error,#dc2626)',fontSize:13}}>{error}</div></div>}

      {result&&(
        <div className="card">
          <div className="card-header" style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div className="card-title">Response</div>
            <span className={'badge badge-'+(result.status>=400?'error':'success')} style={{fontSize:10}}>
              HTTP {result.status}
            </span>
          </div>
          <div className="card-body">
            {reply&&(
              <div style={{whiteSpace:'pre-wrap',fontSize:14,lineHeight:1.55,marginBottom:14}}>{reply}</div>
            )}
            {usage&&(
              <div style={{fontSize:12,color:'var(--text3)',marginBottom:14}}>
                {aiNum(usage.prompt_tokens)} prompt + {aiNum(usage.completion_tokens)} completion
                = {aiNum(usage.total_tokens)} tokens
              </div>
            )}
            <details>
              <summary style={{cursor:'pointer',fontSize:12,color:'var(--text3)'}}>Raw response</summary>
              <pre style={{fontSize:11,overflowX:'auto',marginTop:8}}>
                {typeof result.body==='string'?result.body:JSON.stringify(result.body,null,2)}
              </pre>
            </details>
          </div>
        </div>
      )}
    </div>
  );
}

function PortalAiUsage({subscription}){
  const [data,setData]=useState(null);
  const [error,setError]=useState(null);

  useEffect(()=>{
    if(!subscription||!subscription.id){setData(null);return;}
    let cancelled=false;
    (async()=>{
      try{
        const d=await API('GET','/api/portal/subscriptions/'+subscription.id+'/usage?from=30d');
        if(!cancelled)setData(d);
      }catch(e){if(!cancelled)setError(e.message);}
    })();
    return()=>{cancelled=true;};
  },[subscription&&subscription.id]);

  if(!subscription)return(
    <div className="card"><div className="card-body" style={{fontSize:13,color:'var(--text3)'}}>
      Subscribe to see your consumption.
    </div></div>
  );
  if(error)return <div className="card"><div className="card-body" style={{color:'var(--error,#dc2626)'}}>{error}</div></div>;
  if(!data)return <div className="card"><div className="card-body">Loading…</div></div>;

  const t=data.totals||{};
  return(
    <div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:12,marginBottom:16}}>
        <AiStatTile label="Calls (30d)" value={aiNum(t.calls)}/>
        <AiStatTile label="Total tokens" value={aiNum(t.totalTokens)}/>
        <AiStatTile label="Cost" value={aiMoney(t.costUsd)}/>
      </div>
      <div className="card">
        <div className="card-header"><div className="card-title">By model</div></div>
        <div className="card-body" style={{overflowX:'auto'}}>
          {(data.byModel||[]).length===0?<div style={{color:'var(--text3)',fontSize:13}}>No calls yet.</div>:(
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead>
                <tr style={{textAlign:'left',color:'var(--text3)',fontSize:11,textTransform:'uppercase'}}>
                  <th style={{padding:'8px 6px'}}>Model</th>
                  <th style={{padding:'8px 6px',textAlign:'right'}}>Calls</th>
                  <th style={{padding:'8px 6px',textAlign:'right'}}>Tokens</th>
                  <th style={{padding:'8px 6px',textAlign:'right'}}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.byModel.map((b,i)=>(
                  <tr key={i} style={{borderTop:'1px solid var(--border)'}}>
                    <td style={{padding:'8px 6px',fontFamily:'monospace',fontSize:12}}>{b.bucket||'—'}</td>
                    <td style={{padding:'8px 6px',textAlign:'right'}}>{aiNum(b.calls)}</td>
                    <td style={{padding:'8px 6px',textAlign:'right'}}>{aiNum(b.totalTokens)}</td>
                    <td style={{padding:'8px 6px',textAlign:'right'}}>{aiMoney(b.costUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
